import {
  ATTACHMENT_MIME_TYPES,
  ATTACHMENT_SIZE_LIMIT,
  type AttachmentMimeType,
} from '@/data/db/attachment.schema';

export { ATTACHMENT_SIZE_LIMIT };

/** 超過這個長邊就詢問是否縮小（spec 16.2）。 */
export const MAX_LONG_EDGE = 2560;

const ACCEPTED = new Set<string>(ATTACHMENT_MIME_TYPES);

/** 回傳 null 代表可以接受，否則回傳給使用者看的原因。 */
export function validateImageFile(file: File): string | null {
  if (!ACCEPTED.has(file.type)) {
    return '只接受 JPEG、PNG 與 WebP 圖片。第一版不支援影片、音訊、PDF 與一般辦公文件。';
  }
  if (file.size > ATTACHMENT_SIZE_LIMIT) {
    const mib = (file.size / 1024 / 1024).toFixed(1);
    return `這張圖 ${mib} MB，超過單張 8 MB 的上限。`;
  }
  return null;
}

export interface QuotaDescription {
  label: string;
  full: boolean;
  hint: string;
}

/**
 * 顯示圖片張數；單機版不限制圖片總張數。
 */
export function describeQuota(currentCount: number): QuotaDescription {
  return {
    label: `已使用 ${currentCount} 張`,
    full: false,
    hint: '圖片總張數不設限，請留意本機儲存容量。',
  };
}

export function needsResize(width: number, height: number): boolean {
  return Math.max(width, height) > MAX_LONG_EDGE;
}

/** 等比縮到長邊不超過上限；邊長至少 1px。 */
export function fitWithinLongEdge(
  width: number,
  height: number,
  maxLongEdge: number = MAX_LONG_EDGE,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxLongEdge) return { width, height };

  const ratio = maxLongEdge / longest;
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

/**
 * 預設產生高品質 WebP；需要透明度時保留 PNG（spec 16.2）。
 * 使用者選「保留原圖」時輸出原本的格式。
 */
export function outputMimeTypeFor(
  sourceType: AttachmentMimeType,
  needsTransparency: boolean,
  keepOriginal = false,
): AttachmentMimeType {
  if (keepOriginal) return sourceType;
  if (needsTransparency && sourceType === 'image/png') return 'image/png';
  return 'image/webp';
}

/** 縮圖的長邊；清單、看板與附件列只讀這個。 */
export const THUMBNAIL_LONG_EDGE = 320;

/** WebP 的輸出品質。夠高才看得出是「高品質 WebP」。 */
export const WEBP_QUALITY = 0.9;
